import { AbstractDialogContent } from '@abstracts/dialog/dialog-content';
import { Component, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { PricingDto } from '@dto/pricingDto';
import { NgxSpinnerService } from 'ngx-spinner';
import { NgbModal, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PricingsService } from '@services/entity/pricings.service';
import { ToastService } from '@services/toast/toast.service';
import { plainToClass } from 'class-transformer';
import { NgSelectComponent } from '@ng-select/ng-select';
import { ChargingStationByGroupDto } from '@dto/chargingStationByGroupDto';
import { UserDto } from '@dto/userDto';
import { UsersService } from '@services/entity/users.service';
import { FormService } from '@services/form/form.service';
import { BillingMethodEnum } from '@enums/billingMethodEnum.enum';
import { RemovePricingDialogContentComponent } from '@components/dialog/remove-pricing-dialog/remove-pricing-dialog-content/remove-pricing-dialog-content.component';
import { InvoiceItemDto } from '@dto/invoiceItemDto';

@Component({
  selector: 'app-edit-pricing-dialog-content',
  templateUrl: './edit-pricing-dialog-content.component.html',
  styleUrls: ['./edit-pricing-dialog-content.component.scss']
})
export class EditPricingDialogContentComponent extends AbstractDialogContent implements OnInit {

  public className = 'EditPricingDialogContentComponent';

  public form: UntypedFormGroup;
  public formReady: boolean = false;
  public pricing: PricingDto;
  public chargingStations: ChargingStationByGroupDto[];
  public users: UserDto[] = [];
  public availableUsers: UserDto[] = [];
  public preselectedUsers: UserDto[] = [];
  public pristinePreselectedUsers: UserDto[] = [];
  public removedUsers: UserDto[] = [];
  public openInvoiceItems: InvoiceItemDto[] = [];
  public usersSelectPlaceholder: string = 'Benutzer mit diesem Tarif';
  public isSupporter: boolean = false;

  @Input() public pricingId: number;
  @Input() public customerId: number;
  @Input() public isOperatorView: boolean;
  @ViewChild('chargingStationSelect') chargingStationSelect: NgSelectComponent;

  constructor(
    public activeModal: NgbActiveModal,
    public dialog: NgbModal,
    private pricingsService: PricingsService,
    private spinnerService: NgxSpinnerService,
    private usersService: UsersService,
    private formService: FormService,
    private toastService: ToastService,
    injector: Injector) {
    super(activeModal, injector);
  }

  async ngOnInit() {
    super.ngOnInit();
    this.isSupporter = this.authenticationService.userHasRole('Supporter');
    this.getPricing();
  }

  getPricing() {
    this.pricingsService.getPricing(this.pricingId).subscribe(result => {
      this.pricing = plainToClass(PricingDto, result)
      this.loadUsers();
    }, error => {
      console.error(error)
    })
  }

  async loadUsers() {
    const users = this.isOperatorView
      ? await this.usersService.getUsersOfCustomerAsync(this.customerId)
      : await this.usersService.getUsersOfCurrentCustomerAsync();

    users.forEach(user => {
      const u = plainToClass(UserDto, user);
      this.users.push(u);

      const isEligibleForAvailable = user.pricingId == null || user.pricingId == this.pricingId;
      const isAutomaticBilling = this.pricing.billingMethod == BillingMethodEnum.Automatic;
      const hasEmail = !!user.email;

      if (isEligibleForAvailable && (!isAutomaticBilling || hasEmail)) {
        this.availableUsers.push(u);
      }

      if (user.pricingId == this.pricingId) {
        this.preselectedUsers.push(u);
      }
    });

    this.pristinePreselectedUsers = this.preselectedUsers;

    if (this.availableUsers.length < 1) {
      this.usersSelectPlaceholder = "Alle Benutzer haben bereits einen Tarif";
    }



    this.availableUsers.sort((a, b) => a.fullName.localeCompare(b.fullName));


    this.createForm();
  }

  clear(item: any): void {
    const control = this.form.get('preselectedUsers');

    if (!control) {
      return;
    }

    const currentValues = control.value || [];

    const updatedValues = currentValues.filter((u: any) => u.id !== item.id);

    control.setValue(updatedValues);
    control.markAsDirty();
    control.markAsTouched();
  }

  createForm() {
    this.form = new UntypedFormGroup({
      name: new UntypedFormControl(this.pricing.name),
      description: new UntypedFormControl(this.pricing.description),
      price: new UntypedFormControl({ value: this.pricing.price.toFixed(2), disabled: true }),
      priceDc: new UntypedFormControl({ value: this.pricing.priceDc.toFixed(2), disabled: true }),
      preselectedUsers: new UntypedFormControl(this.preselectedUsers),
    });

    window.setTimeout(() => {
      document.getElementById('name').focus()
    }, 100)

    if (this.isSupporter) this.form.disable();
    this.formReady = true;
  }

  deletePricing() {
    this.spinnerService.show();
    this.pricingsService.deleteEntity(this.pricing.id).subscribe(result => {
      this.spinnerService.hide();
      this.activeModal.close();
      this.toastService.success(`Tarif erfolgreich gelöscht.`);
    }, error => {
      this.spinnerService.hide();
      this.messageService.setError(error);
      this.toastService.error(`Tarif konnte nicht gelöscht werden.`);
    });
  }

  async updatePricing() {
    this.pricing.name = this.form.value.name;
    this.pricing.description = this.form.value.description;
    this.pricing.selectedUsers = this.form.value.preselectedUsers;

    this.removedUsers = this.findRemovedUsers(this.pristinePreselectedUsers, this.pricing.selectedUsers);
    if (this.removedUsers.length > 0) {
      this.pricingsService.getOpenInvoiceItemsOfRecipients(this.pricing.id, this.removedUsers.map(u => u.id)).subscribe(items => {
        this.openInvoiceItems = items;
        const dialogRef = this.dialog.open(RemovePricingDialogContentComponent, { centered: true, backdrop: 'static', size: 'lg' });
        dialogRef.componentInstance.removedUsers = this.removedUsers;
        dialogRef.componentInstance.billingMethod = this.pricing.billingMethod;
        dialogRef.componentInstance.invoiceItems = this.openInvoiceItems;
        if (this.openInvoiceItems.length == 0) {
          dialogRef.componentInstance.step = 2;
        }
        this.pricingsService.pricingUpdatePreparedEvent.subscribe(prepared => {
          if (prepared) this.patchPricing()
          else this.close()
        });
      }, error => {
        console.error(error)
        this.toastService.error($localize`:@@UpdatingPricingErrorToast:Tarif konnte nicht aktualisiert werden.`, 0, true);
      }
      )
    } else {
      this.patchPricing();
    }
  }

  patchPricing() {
    this.spinnerService.show()
    this.pricingsService.patchPricing(this.pricing).subscribe(patchedPricing => {
      this.spinnerService.hide()
      this.pricingsService.emitUpdated(patchedPricing);
      this.toastService.success(`Tarif erfolgreich aktualisiert.`);
      this.activeModal.close()
    }, error => {
      this.spinnerService.hide()
      if (error.Message.includes('active subscription')) {
        this.toastService.error($localize`:@@RemoveActivePricingErrorToast:Benutzer mit aktivem Abonnement müssen den Tarif benutzerseitig kündigen. Sie können dem Benutzer aber die Zugriffsrechte an den Ladestationen entziehen.`, 0, true);
      } else {
        this.toastService.error($localize`:@@UpdatingPricingErrorToast:Tarif konnte nicht aktualisiert werden.`, 0, true);
      }
    })
  }

  close() {
    this.activeModal.close();
  }

  findRemovedUsers(pristine: UserDto[], current: UserDto[]): UserDto[] {
    const currentIds = new Set((current ?? []).map(u => u.id));
    return pristine.filter(user => !currentIds.has(user.id));
  }

  get assignedUsers(): UserDto[] {
    return (this.form?.get('preselectedUsers')?.value as UserDto[]) ?? [];
  }

  getUserDisplayName(user: UserDto): string {
    const match = this.users?.find(u => u.id === user.id) ?? this.availableUsers?.find(u => u.id === user.id);
    const email = match?.email ?? user.email;
    return email ? `${user.fullName} (${email})` : user.fullName;
  }

  trackUserById(_index: number, user: UserDto): string {
    return user.id;
  }

  compareUsersById(a: UserDto, b: UserDto): boolean {
    return a?.id === b?.id;
  }

  userMultiSelectSearchFn = (term: string, item: UserDto): boolean => {
    const selectedUsers = this.form?.get('preselectedUsers')?.value as UserDto[] | undefined;
    if (selectedUsers?.some(u => u.id === item.id)) {
      return false;
    }
    const search = term.toLowerCase();
    return (
      (item.fullName?.toLowerCase().includes(search) ?? false) ||
      (item.email?.toLowerCase().includes(search) ?? false)
    );
  };
}
